import React from 'react';
import { LottieLayer } from '@zola/zola-ui/src/animations/LottieLayer/LottieLayer';
import { LottieProvider } from '@zola/zola-ui/src/animations/LottieLayer/LottieContext';

import { useAnimationContext } from 'components/manage/EditWebsite/EditWebsiteAddEffects/components/AnimationContext/AnimationContext';
import { hexToRgb } from '@zola/zola-ui/src//util/color/hexToRgb';

import { useWebsiteThemeContext } from 'components/publicWebsiteV2/context';
import { StyledOverlayWrapper, StyledGlenardenWrapper } from './OverlayAnimation.styles';
import Glenarden from './LottieAnimations/Glenardenv2.json';

import {
  customizeMonogram,
  customizeBridlewood,
  customizeCreston,
  updateInitialsForInitialsInAssets,
  customizeLocket,
} from './utils';

import { getHeroTitleOverrideStyles } from '../MultiPageHeroContainer/components/MultiPageHeroOnlyText/MultiPageHeroOnlyText.styles';

type OverlayAnimationProps = {
  isSinglePage?: boolean;
  hasBackgroundImage?: boolean;
};

export const OverlayAnimation = (props: OverlayAnimationProps) => {
  const { isSinglePage, hasBackgroundImage } = props;
  const {
    overlayAnimation,
    showAnimationsInEditWebsite,
    overlayAnimationId,
    pageAnimation,
    speed,
    showAnimationPreview,
  } = useAnimationContext();

  const {
    state: {
      components: { globalHeaderFontValues },
      wedding,
    },
  } = useWebsiteThemeContext();

  const { wedding_date, owner_first_name, partner_first_name } = wedding || {};

  const fontColorOverride = getHeroTitleOverrideStyles(!!hasBackgroundImage);
  const fontColorToUse = hexToRgb(fontColorOverride.color || globalHeaderFontValues.color);

  const color: [number, number, number, number] = fontColorToUse
    ? [fontColorToUse.r / 255, fontColorToUse.g / 255, fontColorToUse.b / 255, 1]
    : [0, 0, 0, 1];

  const { overlay_style: overlayCustomizedStyle, key: overlayAnimationKey } =
    overlayAnimation || {};
  const customColorRGB = overlayCustomizedStyle?.color
    ? hexToRgb(overlayCustomizedStyle.color)
    : null;

  // In lottie colors are represented as arrays with values between 0 and 1
  // so we need to divide by 255 for lottie to understand
  const colorToUse: [number, number, number, number] = customColorRGB
    ? [customColorRGB.r / 255, customColorRGB.g / 255, customColorRGB.b / 255, 1]
    : color;

  const firstInitial = owner_first_name?.slice(0, 1);
  const secondInitial = partner_first_name?.slice(0, 1);

  if (!showAnimationsInEditWebsite) {
    return null;
  }

  const speedToUse = speed || 1;

  if (overlayAnimationKey === 'overlay-animation-ampersand') {
    const defaultHeightPercentage = 50;
    const monogramToUse = customizeMonogram({
      animationKey: 'overlay-animation-ampersand',
      initialLayerOne: '.PartnerInitia_Right 2',
      initialLayerTwo: '.PartnerInitia_Left',
      color: colorToUse,
      firstInitial,
      secondInitial,
    });

    return (
      <LottieProvider>
        <StyledOverlayWrapper
          delay={pageAnimation || showAnimationPreview ? speedToUse : 0}
          key={`${overlayAnimationId}-monogram`}
          isSinglePage={isSinglePage}
          heightPercentage={defaultHeightPercentage}
        >
          <LottieLayer data={(monogramToUse as unknown) as Record<string, unknown>} />
        </StyledOverlayWrapper>
      </LottieProvider>
    );
  }

  if (overlayAnimationKey === 'overlay-animation-glenarden') {
    const defaultHeightPercentage = 20;
    return (
      <LottieProvider>
        <StyledGlenardenWrapper
          delay={pageAnimation || showAnimationPreview ? speedToUse : 0}
          key={`${overlayAnimationId}-glenarden`}
          isSinglePage={isSinglePage}
          heightPercentage={defaultHeightPercentage}
        >
          <LottieLayer data={Glenarden} />
        </StyledGlenardenWrapper>
      </LottieProvider>
    );
  }
  if (overlayAnimationKey === 'overlay-animation-creston') {
    const crestonToUse = customizeCreston({
      animationKey: 'overlay-animation-creston',
      initialLayerOne: '.PartnerInitial2',
      initialLayerTwo: '.PartnerInitial1',
      color: colorToUse,
      firstInitial,
      secondInitial,
    });
    const defaultHeightPercentage = 50;

    return (
      <LottieProvider>
        <StyledOverlayWrapper
          delay={pageAnimation || showAnimationPreview ? speedToUse : 0}
          key={`${overlayAnimationId}-creston`}
          isSinglePage={isSinglePage}
          heightPercentage={defaultHeightPercentage}
        >
          <LottieLayer data={(crestonToUse as unknown) as Record<string, unknown>} />
        </StyledOverlayWrapper>
      </LottieProvider>
    );
  }
  if (overlayAnimationKey === 'overlay-animation-willow') {
    const defaultHeightPercentage = 50;

    const willowToUse = updateInitialsForInitialsInAssets({
      animationKey: 'overlay-animation-willow',
      initialLayerOne: 'A',
      initialLayerTwo: 'B',
      color: colorToUse,
      firstInitial,
      secondInitial,
      assetIndex: 65,
    });

    return (
      <LottieProvider>
        <StyledOverlayWrapper
          delay={pageAnimation || showAnimationPreview ? speedToUse : 0}
          key={`${overlayAnimationId}-willow`}
          isSinglePage={isSinglePage}
          heightPercentage={defaultHeightPercentage}
        >
          <LottieLayer data={(willowToUse as unknown) as Record<string, unknown>} />
        </StyledOverlayWrapper>
      </LottieProvider>
    );
  }

  if (overlayAnimationKey === 'overlay-animation-yi-asset') {
    const defaultHeightPercentage = 50;
    const locketToUse = customizeLocket({
      animationKey: 'overlay-animation-yi-asset',
      initialLayerOne: '.PartnerInitial2',
      initialLayerTwo: '.PartnerInitial1',
      color: colorToUse,
      firstInitial,
      secondInitial,
    });
    return (
      <LottieProvider>
        <StyledOverlayWrapper
          delay={pageAnimation || showAnimationPreview ? speedToUse : 0}
          key={`${overlayAnimationId}-locket`}
          isSinglePage={isSinglePage}
          heightPercentage={defaultHeightPercentage}
        >
          <LottieLayer data={(locketToUse as unknown) as Record<string, unknown>} />
        </StyledOverlayWrapper>
      </LottieProvider>
    );
  }

  if (overlayAnimationKey === 'overlay-animation-bridlewood') {
    const defaultHeightPercentage = 20;
    const bridlewoodToUse = customizeBridlewood(wedding_date, colorToUse);
    return (
      <LottieProvider>
        <StyledOverlayWrapper
          delay={pageAnimation || showAnimationPreview ? speedToUse : 0}
          key={`${overlayAnimationId}-Bridlewood`}
          isSinglePage={isSinglePage}
          heightPercentage={defaultHeightPercentage}
        >
          <LottieLayer data={bridlewoodToUse} />
        </StyledOverlayWrapper>
      </LottieProvider>
    );
  }

  return null;
};

export default OverlayAnimation;
